import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import type { AppState, AppAction } from '@/lib/types';
import { useAppState } from '@/hooks/use-app-state';
import { useInsights } from '@/hooks/use-insights';
import { useSheetsSync, type SheetsStatus } from '@/hooks/use-sheets-sync';
import { calcTrainingInsights } from '@/lib/analytics';

type InsightsResult = ReturnType<typeof calcTrainingInsights>;

export interface AppContextValue {
  state: AppState;
  dispatch: (action: AppAction) => void;
  insights: InsightsResult | null;
  sheetsStatus: SheetsStatus;
  isOnline: boolean;
  manualSync: () => Promise<void>;
}

export const AppContext = createContext<AppContextValue | null>(null);

export function useApp(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}

export function AppProvider({ children }: { children: ReactNode }) {
  const { state, dispatch: rawDispatch } = useAppState();
  const insights = useInsights(state);
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);

  const { sheetsStatus, manualSync, setLastAction } = useSheetsSync(state, rawDispatch);

  // Wrap dispatch to track the last action type for sync decisions
  const dispatch = useCallback((action: AppAction) => {
    setLastAction(action.type);
    rawDispatch(action);
  }, [rawDispatch, setLastAction]);

  useEffect(() => {
    const online = () => setIsOnline(true);
    const offline = () => setIsOnline(false);
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    return () => {
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
    };
  }, []);

  return (
    <AppContext.Provider value={{ state, dispatch, insights, sheetsStatus, isOnline, manualSync }}>
      {children}
    </AppContext.Provider>
  );
}
